import * as React from 'react';
import isNull from 'lodash/isNull';
import omitBy from 'lodash/omitBy';

import {
  CollectionSurveyQuestions,
  CollectionSurveyQuestionType
} from '@src/generated/hooks';
import { TypeEmail } from './TypeEmail';
import { TypeImage } from './TypeImage';
import { TypeMultiChoice } from './TypeMultiChoice';
import { TypeNumber } from './TypeNumber';
import { TypePhone } from './TypePhone';
import { TypeProducts } from './TypeProducts';
import { TypeProgress } from './TypeProgress';
import { TypeSingleChoice } from './TypeSingleChoice';
import { TypeSubQuestion } from './TypeSubQuestion';
import { TypeText } from './TypeText';
import { TypeTextarea } from './TypeTextarea';

export interface SurveyQuestionProps {
  question: CollectionSurveyQuestions;
}

/**
 * @name SurveyQuestion
 * @description This component handles rendering a specific question of
 * any available type. Each of the `TypeXxxx` components extend this
 * components props and handle rendering the specific question type UI.
 */
export const SurveyQuestion = (props: SurveyQuestionProps) => {
  // Setup
  const question = omitBy(props.question, isNull);

  // Markup
  const NullComponent = () => {
    // TODO: Log an error, we should never have to render this one
    // return null;

    return (
      <div className="p-8 text-rust">
        No component found for type <b>{question.type as string}</b>.
      </div>
    );
  };

  const types: Record<string, any> = {
    [CollectionSurveyQuestionType.Email]: TypeEmail,
    [CollectionSurveyQuestionType.Image]: TypeImage,
    [CollectionSurveyQuestionType.MultiChoice]: TypeMultiChoice,
    [CollectionSurveyQuestionType.Number]: TypeNumber,
    [CollectionSurveyQuestionType.Phone]: TypePhone,
    [CollectionSurveyQuestionType.Products]: TypeProducts,
    [CollectionSurveyQuestionType.Progress]: TypeProgress,
    [CollectionSurveyQuestionType.SingleChoice]: TypeSingleChoice,
    [CollectionSurveyQuestionType.SubQuestions]: TypeSubQuestion,
    [CollectionSurveyQuestionType.Text]: TypeText,
    [CollectionSurveyQuestionType.Textarea]: TypeTextarea,

    // No UI component required
    [CollectionSurveyQuestionType.QueryParam]: NullComponent,
    [CollectionSurveyQuestionType.Uuid]: NullComponent
  };

  // We provide a Null fallback component to prevent errors
  const Component = types[question.type as string] || NullComponent;

  return <Component question={question} />;
};
